import "./insights.css";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFileAlt, FaGem, FaMagic, FaChartBar, FaBolt, FaDollarSign, FaCalendarAlt, FaCalendar } from "react-icons/fa";
import InformeMarkdown from "./InsightMarkdown";
import { useDispatch, useSelector } from "react-redux";
import { getEnergyReport } from "../../redux/apiCalls/billsReportCall";
import {
  getConsumptionData,
  getData,
  getGeneralData,
} from "../../redux/apiCalls/dataCall";
import Swal from "sweetalert2";
import { formatDataArray } from "../../helpers/functions";
import { publicRequest } from "../../requestMeth";

const iconMap = {
  "Prorrateo-2024": FaChartBar,
  "Prorrateo-2025": FaChartBar,
  "Energia": FaBolt,
  "Costos": FaDollarSign,
};

const formatSimple = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastMonths = (count = 3) => {
  const today = new Date();
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    return {
      label: date.toLocaleString("es-AR", { month: "long", year: "numeric" }),
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`,
      startDate: formatSimple(new Date(date.getFullYear(), date.getMonth(), 1)),
      endDate: formatSimple(
        new Date(date.getFullYear(), date.getMonth() + 1, 0)
      ),
    };
  });
};

const Insights = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const { prorrateo2025, prorrateo2024, generalData } = useSelector(
    (state) => state.data
  );
  const energyReport = useSelector((state) => state.billsReport?.energyReport);

  const [loading, setLoading] = useState(false);
  const [existingReportInsight, setExistingReportInsight] = useState("");

  const formatProrrateoData = (groupedData) =>
    Object.fromEntries(
      Object.entries(groupedData).map(([month, items]) => [
        month,
        items.map((item) => ({
          sucursal: item.office,
          consumo: parseFloat(item.consumoTotalMes),
          unidad: item.unidad || "",
          servicio: item.tiposConsumo.includes("Volumen Registrado")
            ? "Gas"
            : item.tiposConsumo.includes("Agua Consumida")
            ? "Agua"
            : "Electricidad",
          days: item.diasCalculados,
        })),
      ])
    );

  const getProrrateoData = async (year, currentData) => {
    if (!currentData) {
      const res = await getConsumptionData(dispatch, user?.accessToken, year);
      return formatProrrateoData(res.groupedByMonth);
    }
    return formatProrrateoData(currentData.groupedByMonth);
  };

  const handleDownload = async (type) => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    Swal.fire({
      icon: "info",
      title: "Se está generando el informe. Por favor, aguarde.",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 30000,
      timerProgressBar: true,
      didOpen: () => Swal.showLoading(),
    });

    setLoading(true);
    try {
      let sendData = null;

      if (type === "Prorrateo-2025") {
        sendData = await getProrrateoData("2025", prorrateo2025);
      } else if (type === "Prorrateo-2024") {
        sendData = await getProrrateoData("2024", prorrateo2024);
      } else if (type === "Energia" && !energyReport) {
        sendData = await getEnergyReport(dispatch, user?.accessToken, {
          year: 2025,
          service: "ELECTRICIDAD",
          supplierSelected: "EDENOR",
        });
      } else if (!generalData) {
        sendData = await getGeneralData(dispatch, user?.accessToken);
      }

      const payload = {
        data: type.includes("Prorrateo")
          ? sendData
          : type === "Energia"
          ? energyReport || sendData
          : generalData || sendData,
        options: {
          title:
            type === "Prorrateo-2025"
              ? "Prorrateo de consumos por mes 2025"
              : type === "Prorrateo-2024"
              ? "Prorrateo de consumos por mes 2024"
              : type === "Energia"
              ? "Informe de energía consumida"
              : "Comparativa de costos con año anterior",
          idCompany: user?.company,
          month,
        },
      };

      const { data } = await publicRequest.post("/insight/data", payload, {
        headers: {
          "Content-Type": "application/json",
          token: `Bearer ${user?.accessToken}`,
        },
      });

      setExistingReportInsight({
        ...data.insight,
        user: user?.user,
        date: new Date().toLocaleDateString("es-AR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
      });
    } catch (err) {
      console.error("Error al descargar informe:", err);
    } finally {
      Swal.close();
      setLoading(false);
    }
  };

  const handleMonthlyDownload = async (monthObj) => {
    const { startDate, endDate, value: month } = monthObj;

    try {
      setLoading(true);
      Swal.fire({
        title: "Generando informe mensual...",
        timer: 30000,
        didOpen: () => Swal.showLoading(),
      });

      const dataInvoices = await getData(dispatch, user?.accessToken, {
        startDate,
        endDate,
        provinces: [],
        companies: [],
        utilities: [],
        class: [],
        report: true,
      });

      const payload = {
        data: formatDataArray(dataInvoices),
        options: {
          title: `Informe mensual ${month}`,
          idCompany: user?.company,
          month,
        },
      };

      const { data } = await publicRequest.post("/insight/data", payload, {
        headers: {
          "Content-Type": "application/json",
          token: `Bearer ${user?.accessToken}`,
        },
      });

      setExistingReportInsight({
        ...data.insight,
        user: user?.user,
        date: new Date().toLocaleDateString("es-AR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
      });
    } catch (err) {
      console.error(err);
    } finally {
      Swal.close();
      setLoading(false);
    }
  };

  return (
    <div className="users-list insights">
      <div className="cont-back cont-back-users" onClick={() => navigate(`/`)}>
        <i>
          <FaArrowLeft />
        </i>
        <p>Escritorio</p>
      </div>
      <div className="insights-form">
        <h6 className="insights-form-title">
          Informes sobre reportes existentes
        </h6>
        <span className="insights-premium">
        <i>
          <FaGem />
        </i>
        Prueba gratuita hasta 01/08/2025. Para continuar utilizando la sección
        de informes, por favor contactanos para activar la suscripción.
      </span>
        <div className="insights-file-button">
          <div className="dropdown-insights">
            <button className="button-download-insight" disabled={loading}>
              <i className="icon-download-insight">
                <FaCalendarAlt />
              </i>
              <span>Informe mensual ▾</span>
            </button>
            <div className="dropdown-insights-content">
              {getLastMonths().map((month, i) => (
                <button key={i} onClick={() => handleMonthlyDownload(month)}>
                  {month.label}
                </button>
              ))}
            </div>
          </div>
          {["Prorrateo-2025", "Energia", "Costos"].map(
            (type, i) => {
              const Icon = iconMap[type] || FaMagic; // FaMagic como fallback
              return (
                <button
                  className="button-download-insight"
                  key={i}
                  onClick={() => handleDownload(type)}
                  disabled={loading}
                >
                  <i className="icon-download-insight">
                    <Icon />
                  </i>
                  <span>{type.replace("-", " ")}</span>
                </button>
              );
            }
          )}
        </div>
      </div>

      {existingReportInsight && (
        <div className="insight-result mt-4">
          <InformeMarkdown content={existingReportInsight} />
        </div>
      )}
    </div>
  );
};

export default Insights;
